import {
  AnniversaryIntervalEnum,
  BlankEnum,
  Bonus,
  BonusCreateRequest,
  BonusMessageTemplateRequest,
  BonusStatusEnum,
  FrequencyEnum,
  IntervalEnum,
  NullEnum,
  PatchedBonusRequest,
  PayoutTypeEnum,
  SalaryTypeEnum,
  SpecialOccasionTypeEnum,
  TaxTreatmentEnum
} from '@pxtra-client-lib-legacy/gen';
import { makeAutoObservable } from 'mobx';
import Util from '../../../../misc/util';

export const TAX_TREATMENTS: TaxTreatmentEnum[] = [
  'RELIEF_PREMIUM_2026',
  'SPECIAL_OCCASION',
  'NON_CASH_GIFT',
  'NON_CASH_BENEFIT',
  'FULLY_TAXED'
];

export const SPECIAL_OCCASION_TYPES: SpecialOccasionTypeEnum[] = [
  'BIRTHDAY',
  'ANNIVERSARY',
  'CHILD_BIRTH',
  'BAPTISM',
  'WEDDING',
  'SILVER_WEDDING',
  'GOLDEN_WEDDING',
  'RELATIVE_BIRTHDAY',
  'SCHOOL_ENROLLMENT',
  'COMMUNION',
  'PROTESTANT_CONFIRMATION',
  'CATHOLIC_CONFIRMATION',
  'INAUGATION',
  'POSITION_CHANGE',
  'FAREWELL',
  'PASSED_FINAL_EXAM',
  'PROMOTION'
];

export const PARTIAL_SPECIAL_OCCASION_TYPES: SpecialOccasionTypeEnum[] = [
  'CHILD_BIRTH',
  'BAPTISM',
  'WEDDING',
  'SILVER_WEDDING',
  'GOLDEN_WEDDING',
  'RELATIVE_BIRTHDAY',
  'SCHOOL_ENROLLMENT',
  'COMMUNION',
  'PROTESTANT_CONFIRMATION',
  'CATHOLIC_CONFIRMATION',
  'INAUGATION',
  'POSITION_CHANGE',
  'FAREWELL',
  'PASSED_FINAL_EXAM',
  'PROMOTION'
];

export const PAYOUT_TYPES: PayoutTypeEnum[] = ['APP_VOUCHER', 'EXTERNAL', 'SALARY'];

export const SALARY_TYPES: SalaryTypeEnum[] = [
  'BONUS_RECURRING',
  'PERFORMANCE_BONUS',
  'PREMIUM_COMMISSION',
  'PREMIUM_COMMISSION_ONE_TIME',
  'ON_CALL_DUTY',
  'SPECIAL_PAYMENT',
  'ROYALTY',
  'REVENUE_SHARE_RECURRING',
  'REVENUE_SHARE_ONE_TIME',
  'VACATION_PAY',
  'VARIABLE_AGREEMENT_RECURRING',
  'VARIABLE_AGREEMENT_ONE_TIME',
  'CHRISTMAS_BONUS',
  'ALLOWANCE'
];

class CreateBonusStore {
  private _title = '';
  private _description: string | undefined = undefined;

  private _taxTreatment: TaxTreatmentEnum | undefined = undefined;
  private _specialOccasionType: SpecialOccasionTypeEnum | BlankEnum | NullEnum | undefined =
    undefined;
  private _payoutType: PayoutTypeEnum | undefined = undefined;

  private _amount: number | undefined = undefined;
  private _eventMonth: Date | null | undefined = undefined;
  private _payoutMonth: Date | null | undefined = undefined;
  private _startAccountingPeriod: Date | null | undefined = undefined;
  private _endAccountingPeriod: Date | null | undefined = undefined;
  private _interval: Array<IntervalEnum> | null | undefined = undefined;
  private _anniversaryInterval: AnniversaryIntervalEnum | NullEnum | undefined = undefined;
  private _frequency: FrequencyEnum | NullEnum | undefined | null = undefined;
  private _salaryType: SalaryTypeEnum | NullEnum | undefined = undefined;
  private _salaryTypeId: string | undefined = undefined;

  private _employees: string[] | undefined = undefined;
  private _initialEmployees: string[] | undefined = undefined;

  private _messageTemplate: BonusMessageTemplateRequest | undefined = undefined;
  private _selectedTemplateId: string | undefined = undefined;
  private _message: string = '';

  private _editingId: string | undefined = undefined;
  private _status: BonusStatusEnum | undefined = undefined;
  private _changedFields: string[] = [];

  private readonly fieldMapping = {
    title: () => this._title,
    description: () => this._description,
    tax_treatment: () => this._taxTreatment,
    special_occasion_type: () => this._specialOccasionType,
    payout_type: () => this._payoutType,
    amount: () => this._amount,
    frequency: () => this._frequency,
    event_month: () => Util.dateToMonthField(this._eventMonth),
    payout_month: () => Util.dateToMonthField(this._payoutMonth),
    start_accounting_period: () => Util.dateToMonthField(this._startAccountingPeriod),
    end_accounting_period: () => Util.dateToMonthField(this._endAccountingPeriod),
    interval: () => this._interval,
    anniversary_interval: () => this._anniversaryInterval,
    salary_type: () => this._salaryType,
    salary_type_id: () => this._salaryTypeId,
    employees: () => this._employees,
    message: () => this._message
  };

  get title(): string {
    return this._title;
  }
  set title(value: string) {
    this._title = value;
    this._changedFields.push('title');
  }

  get description(): string | undefined {
    return this._description;
  }
  set description(value: string | undefined) {
    this._description = value;
    this._changedFields.push('description');
  }

  get taxTreatment(): TaxTreatmentEnum | undefined {
    return this._taxTreatment;
  }
  set taxTreatment(value: TaxTreatmentEnum | undefined) {
    this._taxTreatment = value;
    this._changedFields.push('tax_treatment');
  }

  get specialOccasionType(): SpecialOccasionTypeEnum | NullEnum | undefined {
    return this._specialOccasionType;
  }
  set specialOccasionType(value: SpecialOccasionTypeEnum | NullEnum | undefined) {
    this._specialOccasionType = value;
    this._changedFields.push('special_occasion_type');
  }

  get payoutType(): PayoutTypeEnum | undefined {
    return this._payoutType;
  }
  set payoutType(value: PayoutTypeEnum | undefined) {
    this._payoutType = value;
    this._changedFields.push('payout_type');
  }

  get amount(): number | undefined {
    return this._amount;
  }
  set amount(value: number | undefined) {
    this._amount = value;
    this._changedFields.push('amount');
  }

  get eventMonth(): Date | null | undefined {
    return this._eventMonth;
  }
  set eventMonth(value: Date | null | undefined) {
    this._eventMonth = value;
    this._changedFields.push('event_month');
  }

  get payoutMonth(): Date | null | undefined {
    return this._payoutMonth;
  }
  set payoutMonth(value: Date | null | undefined) {
    this._payoutMonth = value;
    this._changedFields.push('payout_month');
  }

  get startAccountingPeriod(): Date | null | undefined {
    return this._startAccountingPeriod;
  }
  set startAccountingPeriod(value: Date | null | undefined) {
    this._startAccountingPeriod = value;
    this._changedFields.push('start_accounting_period');
  }

  get endAccountingPeriod(): Date | null | undefined {
    return this._endAccountingPeriod;
  }
  set endAccountingPeriod(value: Date | null | undefined) {
    this._endAccountingPeriod = value;
    this._changedFields.push('end_accounting_period');
  }

  get interval(): Array<IntervalEnum> | null | undefined {
    return this._interval;
  }
  set interval(value: Array<IntervalEnum> | null | undefined) {
    this._interval = value;
    this._changedFields.push('interval');
  }

  get anniversaryInterval(): AnniversaryIntervalEnum | NullEnum | undefined {
    return this._anniversaryInterval;
  }
  set anniversaryInterval(value: AnniversaryIntervalEnum | NullEnum | undefined) {
    this._anniversaryInterval = value;
    this._changedFields.push('anniversary_interval');
  }

  get frequency(): FrequencyEnum | NullEnum | undefined | null {
    return this._frequency;
  }
  set frequency(value: FrequencyEnum | NullEnum | undefined | null) {
    this._frequency = value;
    this._changedFields.push('frequency');
  }

  get salaryType(): SalaryTypeEnum | NullEnum | undefined {
    return this._salaryType;
  }
  set salaryType(value: SalaryTypeEnum | NullEnum | undefined) {
    this._salaryType = value;
    this._changedFields.push('salary_type');
  }

  get salaryTypeId(): string | undefined {
    return this._salaryTypeId;
  }
  set salaryTypeId(value: string | undefined) {
    this._salaryTypeId = value;
    this._changedFields.push('salary_type_id');
  }

  get employees(): string[] | undefined {
    return this._employees;
  }
  set employees(value: string[] | undefined) {
    const currentIds = this._employees ?? [];
    const newIds = value ?? [];
    const isSameLength = currentIds.length === newIds.length;
    const hasSameElements = isSameLength && currentIds.every((id) => newIds.includes(id));

    if (!hasSameElements) {
      this._employees = value;
      this._changedFields.push('employees');
    }
  }

  get initialEmployees(): string[] | undefined {
    return this._initialEmployees;
  }

  setInitialEmployees(employees: string[] | undefined) {
    if (this._initialEmployees !== undefined) return;
    this._initialEmployees = employees ? [...employees] : undefined;
  }

  get editingId(): string | undefined {
    return this._editingId;
  }
  set editingId(value: string | undefined) {
    this._editingId = value;
  }

  get editingNotAllowed(): boolean {
    if (this._status) {
      return this._status !== 'WAITING';
    } else {
      return false;
    }
  }

  get changedFields(): string[] {
    return this._changedFields;
  }

  get messageTemplate(): BonusMessageTemplateRequest | undefined {
    return this._messageTemplate;
  }
  set messageTemplate(value: BonusMessageTemplateRequest | undefined) {
    this._messageTemplate = value;
    this._changedFields.push('message_template');
  }

  get selectedTemplateId(): string | undefined {
    return this._selectedTemplateId;
  }
  set selectedTemplateId(value: string | undefined) {
    this._selectedTemplateId = value;
    this._changedFields.push('selected_template_id');
  }

  get message(): string {
    return this._message;
  }
  set message(value: string) {
    this._message = value;
    this._changedFields.push('message');
  }

  public buildPostRequest(): BonusCreateRequest {
    const request = Object.fromEntries(
      Object.entries(this.fieldMapping)
        .map(([key, getValue]) => [key, getValue()])
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    );
    return request as BonusCreateRequest;
  }

  public buildPatchRequest(): PatchedBonusRequest {
    const request: Record<string, unknown> = {};

    this._changedFields.forEach((field) => {
      const key = field as keyof typeof this.fieldMapping;
      const getValue = this.fieldMapping[key];

      if (getValue) {
        const value = getValue();
        request[key] = value === undefined ? null : value;
      }
    });

    return request as PatchedBonusRequest;
  }

  public loadBonus: (bonus: Bonus | undefined | null) => void = (bonus) => {
    if (bonus) {
      this._title = bonus.title ?? '';
      this._description = bonus.description ?? undefined;
      this._taxTreatment = bonus.tax_treatment ?? undefined;
      this._specialOccasionType = bonus.special_occasion_type ?? undefined;
      this._payoutType = bonus.payout_type ?? undefined;

      this._amount = bonus.amount ?? undefined;
      this._frequency = bonus.frequency;
      this._eventMonth = bonus.event_month ? Util.monthFieldToDate(bonus.event_month) : undefined;
      this._payoutMonth = bonus.payout_month
        ? Util.monthFieldToDate(bonus.payout_month)
        : undefined;
      this._startAccountingPeriod = bonus.start_accounting_period
        ? Util.monthFieldToDate(bonus.start_accounting_period)
        : undefined;
      this._endAccountingPeriod = bonus.end_accounting_period
        ? Util.monthFieldToDate(bonus.end_accounting_period)
        : undefined;
      this._interval = bonus.interval ?? undefined;
      this._anniversaryInterval = bonus.anniversary_interval ?? undefined;
      this._salaryType = bonus.salary_type ?? undefined;
      this._salaryTypeId = bonus.salary_type_id ?? undefined;

      this._employees = bonus.employees ?? undefined;
      this.setInitialEmployees(bonus.employees);

      this._message = bonus.message ?? undefined;

      this._editingId = bonus.id;
      this._status = bonus.status;
    }
  };

  public resetPayoutType() {
    this._payoutType = undefined;
    this._amount = undefined;
    this._eventMonth = undefined;
    this._payoutMonth = undefined;
    this._startAccountingPeriod = undefined;
    this._endAccountingPeriod = undefined;
    this._interval = undefined;
    this._anniversaryInterval = undefined;
    this._frequency = undefined;
    this._salaryType = undefined;
    this._salaryTypeId = undefined;
  }

  public clear() {
    this._title = '';
    this._description = undefined;
    this._taxTreatment = undefined;
    this._specialOccasionType = undefined;
    this._payoutType = undefined;
    this._amount = undefined;
    this._frequency = undefined;
    this._eventMonth = undefined;
    this._payoutMonth = undefined;
    this._startAccountingPeriod = undefined;
    this._endAccountingPeriod = undefined;
    this._interval = undefined;
    this._anniversaryInterval = undefined;
    this._salaryType = undefined;
    this._salaryTypeId = undefined;
    this._employees = undefined;
    this._initialEmployees = undefined;
    this._message = '';
    this._messageTemplate = undefined;
    this._selectedTemplateId = undefined;
    this._editingId = undefined;
    this._status = undefined;
    this._changedFields = [];
  }

  constructor() {
    makeAutoObservable(this);
  }
}

export default CreateBonusStore;
